import { isEqual, identity } from 'lodash'

import { sortCardsInBeat } from '../helpers/cards'
import {
  ADD_CARD,
  EDIT_CARD_DETAILS,
  EDIT_CARD_COORDINATES,
  CHANGE_LINE,
  CHANGE_BEAT,
  CHANGE_BOOK,
  REORDER_CARDS_WITHIN_LINE,
  REORDER_CARDS_IN_BEAT,
  DELETE_CARD,
  ATTACH_CHARACTER_TO_CARD,
  REMOVE_CHARACTER_FROM_CARD,
  ATTACH_PLACE_TO_CARD,
  REMOVE_PLACE_FROM_CARD,
  ATTACH_TAG_TO_CARD,
  REMOVE_TAG_FROM_CARD,
  ADD_CARD_IN_BEAT,
  LOAD_CARDS,
  LOAD_CARD,
  BATCH_LOAD_CARD,
  REMOVE_CARD,
  EDIT_CARD_TEMPLATE_ATTRIBUTE,
  ADD_TEMPLATE_TO_CARD,
  REMOVE_TEMPLATE_FROM_CARD,
  DUPLICATE_CARD,
  MOVE_CARD_TO_BOOK,
  EDIT_CARD_TITLE,
  EDIT_CARD_DESCRIPTION,
  EDIT_CARD_CUSTOM_ATTRIBUTE,
  REORDER_CARD_TEMPLATE_ATTRIBUTES,
  REORDER_OUTLINE_PLAN_CARDS,
  AUTO_SORT_BEAT,
  REMOVE_NOTE_FROM_CARD,
  ATTACH_NOTE_TO_CARD,
} from '../constants/ActionTypes'

import selectors from '../selectors'

/**
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').TextAttributeData} TextAttributeData
 * @typedef {import('../index').Slate} Slate
 * @typedef {import('../index').DOMSelection} DOMSelection
 * @typedef {import('../index').SlateSelection} SlateSelection
 * @typedef {import('../index').Dispatch}Dispatch
 * @typedef {import('../index').GetState}GetState
 * @typedef {import('../types/system').SceneTemplate} SceneTemplate
 * @typedef {import('../types/system').SceneCustomTemplate} SceneCustomTemplate
 */

/**
 * @typedef {{ type: 'ATTACH_CHARACTER_TO_CARD', id: number, characterId: number, currentTimeline: 'series' | number }} AttachCharacterToCardAction
 * @typedef {{ type: 'AUTO_SORT_BEAT', id: number, bookId: 'series' | number, isSeries: boolean }} AutoSortBeatAction
 * @typedef {{ type: 'MOVE_CARD_TO_BOOK', bookId: number | 'series', cardId: number, destinationBeatId: number, destinationLineId: number }} MoveCardToBookAction
 */

/**
 * @typedef {ReturnType<typeof addCard> | ReturnType<typeof addNewCardInBeat> | ReturnType<typeof editCard> | ReturnType<typeof editCardAttributes> | ReturnType<typeof editCardCustomAttribute> | ReturnType<typeof editCardDescription> | ReturnType<typeof editCardTitle> | ReturnType<typeof editCardCoordinates> | ReturnType<typeof addTemplateToCard> | ReturnType<typeof editCardTemplateAttribute> | ReturnType<typeof changeLine> | ReturnType<typeof changeBeat> | ReturnType<typeof changeBook> | ReturnType<typeof reorderCardsWithinLine> | ReturnType<typeof reorderCardsInBeat> | ReturnType<typeof deleteCard> | ReturnType<typeof addPlace> | ReturnType<typeof addTag> | ReturnType<typeof removeCharacter> | ReturnType<typeof removePlace> | ReturnType<typeof removeTag> | ReturnType<typeof addNote> | ReturnType<typeof removeNote> | ReturnType<typeof removeTemplateFromCard> | ReturnType<typeof load> | ReturnType<typeof loadSingle> | ReturnType<typeof batchLoad> | ReturnType<typeof removeSingle> | ReturnType<typeof duplicateCard> | ReturnType<typeof reorderCardTemplateAttribute> | ReturnType<typeof _reorderOutlinePlanCards> | AutoSortBeatAction | AttachCharacterToCardAction | MoveCardToBookAction} CardsActions
 */

const {
  currentTimelineSelector,
  allCardsInBeatSelector,
  singleBeatInCurrentBookSelector,
  sortedLinesByBookSelector,
} = selectors(identity)

/**
 * @param {Partial<Card>} card
 * @param {boolean} addMissingBeats
 * @return {{ type: 'ADD_CARD', card: Partial<Card>, addMissingBeats: boolean }}
 */
export function addCard(card, addMissingBeats) {
  return { type: ADD_CARD, card, addMissingBeats }
}

/**
 * @param {Card} newCard
 * @param {Array<number | Null>} reorderIds (Yes.  This type is that weird.  It's used to insert a new card into a position by it's position in an array. /shrug)
 * @return {{ type: 'ADD_CARD_IN_BEAT', newCard: Card, reorderIds: Array<number | Null> }}
 */
export function addNewCardInBeat(newCard, reorderIds) {
  return { type: ADD_CARD_IN_BEAT, newCard, reorderIds }
}

/**
 * @param {number} id
 * @param {String} title
 * @param {Slate | String} description
 * @param {Array<SceneTemplate | SceneCustomTemplate>} templates
 * @param {Partial<Card>} attrs
 * @return {{ type: 'EDIT_CARD_DETAILS', id: number, attributes: { title: String, description: Slate | String, templates: Array<SceneTemplate | SceneCustomTemplate> }} & Partial<Card> }
 */
export function editCard(id, title, description, templates, attrs) {
  return { type: EDIT_CARD_DETAILS, id, attributes: { title, description, templates, ...attrs } }
}

/**
 * @typedef CardUpdateValue
 * @property {Card[keyof Card]} value
 * @property {DOMSelection | SlateSelection} [selection]
 */

/**
 * @typedef {Record<keyof Card, CardUpdateValue>} UpdateCardAttributes
 */

/**
 * @param {number} id
 * @param {Partial<UpdateCardAttributes>} attributes
 * @return {{ type: 'EDIT_CARD_DETAILS', id: number, attributes: Partial<UpdateCardAttributes> }}
 */
export function editCardAttributes(id, attributes) {
  return {
    type: EDIT_CARD_DETAILS,
    id,
    attributes,
  }
}

/**
 * @param {number} id
 * @param {String} name
 * @param {TextAttributeData} value
 * @param {DOMSelection | SlateSelection} selection
 * @return {{ type: 'EDIT_CARD_CUSTOM_ATTRIBUTE', id: number, name: String, value: TextAttributeData, selection: DOMSelection | SlateSelection }}
 */
export function editCardCustomAttribute(id, name, value, selection) {
  return {
    type: EDIT_CARD_CUSTOM_ATTRIBUTE,
    id,
    name,
    value,
    selection,
  }
}

/**
 * @param {number} id
 * @param {TextAttributeData} description
 * @param {SlateSelection | Null} selection
 * @return {{ type: 'EDIT_CARD_DESCRIPTION', id: number, description: TextAttributeData, selection: SlateSelection | Null }}
 */
export function editCardDescription(id, description, selection) {
  return {
    type: EDIT_CARD_DESCRIPTION,
    id,
    description,
    selection,
  }
}

/**
 * @param {number} id
 * @param {String} newTitle
 * @param {DOMSelection | Null} selection
 * @return {{ type: 'EDIT_CARD_TITLE', id: number, newTitle: String, selection: DOMSelection | Null }}
 */
export function editCardTitle(id, newTitle, selection) {
  return {
    type: EDIT_CARD_TITLE,
    id,
    newTitle,
    selection,
  }
}

/**
 * @param {number} id
 * @param {number} lineId
 * @param {number} beatId
 * @param {number | 'series'} bookId
 * @return {{ type: 'EDIT_CARD_COORDINATES', id: number, lineId: number, beatId: number, bookId: number | 'series' }}
 */
export function editCardCoordinates(id, lineId, beatId, bookId) {
  return { type: EDIT_CARD_COORDINATES, id, lineId, beatId, bookId }
}

/**
 * @param {number} id
 * @param {SceneTemplate | SceneCustomTemplate} templateData
 * @return {{ type: 'ADD_TEMPLATE_TO_CARD', id: number, templateData: SceneTemplate | SceneCustomTemplate }}
 */
export function addTemplateToCard(id, templateData) {
  return { type: ADD_TEMPLATE_TO_CARD, id, templateData }
}

/**
 * @param {number} id
 * @param {String} templateId
 * @param {String} name
 * @param {TextAttributeData} value
 * @param {DOMSelection | SlateSelection} selection
 * @return {{ type: 'EDIT_CARD_TEMPLATE_ATTRIBUTE', id: number, templateId: String, name: String, value: TextAttributeData, selection: DOMSelection | SlateSelection }}
 */
export function editCardTemplateAttribute(id, templateId, name, value, selection) {
  return {
    type: EDIT_CARD_TEMPLATE_ATTRIBUTE,
    id,
    templateId,
    name,
    value,
    selection,
  }
}

/**
 * @param {number} id
 * @param {number} lineId
 * @param {number | 'series'} bookId
 * @return {{ type: 'CHANGE_LINE', id: number, lineId: number, bookId: number | 'series' }}
 */
export function changeLine(id, lineId, bookId) {
  return { type: CHANGE_LINE, id, lineId, bookId }
}

/**
 * @param {number} id
 * @param {number} beatId
 * @param {number | 'series'} bookId
 * @return {{ type: 'CHANGE_BEAT', id: number, beatId: number, bookId: number | 'series' }}
 */
export function changeBeat(id, beatId, bookId) {
  return { type: CHANGE_BEAT, id, beatId, bookId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'CHANGE_BOOK', id: number, bookId: number | 'series' }}
 */
export function changeBook(id, bookId) {
  return { type: CHANGE_BOOK, id, bookId }
}

/**
 * @param {number} beatId
 * @param {number} lineId
 * @param {Array<number>} ids
 * @param {boolean} addMissingBeats
 * @return {{ type: 'REORDER_CARDS_WITHIN_LINE', beatId: number, lineId: number, ids: Array<number>, addMissingBeats: boolean }}
 */
export function reorderCardsWithinLine(beatId, lineId, ids, addMissingBeats) {
  return { type: REORDER_CARDS_WITHIN_LINE, beatId, lineId, ids, addMissingBeats }
}

/**
 * @param {number} beatId
 * @param {number} lineId
 * @param {Array<number>} newOrderInBeat
 * @param {Array<number>} newOrderWithinLine
 * @param {number} newIdInBeat
 * @param {number | 'series'} bookId
 * @return {{ type: 'REORDER_CARDS_IN_BEAT', beatId: number, lineId: number, newOrderInBeat: Array<number>, newOrderWithinLine: Array<number>, newIdInBeat: number, bookId: number | 'series'}}
 */
export function reorderCardsInBeat(
  beatId,
  lineId,
  newOrderInBeat,
  newOrderWithinLine,
  newIdInBeat,
  bookId
) {
  return {
    type: REORDER_CARDS_IN_BEAT,
    beatId,
    lineId,
    newOrderInBeat,
    newOrderWithinLine,
    newIdInBeat,
    bookId,
  }
}

/**
 * @param {number} id
 * @return {{ type: 'DELETE_CARD', id: number }}
 */
export function deleteCard(id) {
  return { type: DELETE_CARD, id }
}

/**
 * @param {number} id
 * @param {number} characterId
 * @return {{ type: 'ATTACH_CHARACTER_TO_CARD', id: number, characterId: number }}
 */
export function addCharacter(id, characterId) {
  return { type: ATTACH_CHARACTER_TO_CARD, id, characterId }
}

/**
 * @param {number} id
 * @param {number} placeId
 * @return {{ type: 'ATTACH_PLACE_TO_CARD', id: number, placeId: number }}
 */
export function addPlace(id, placeId) {
  return { type: ATTACH_PLACE_TO_CARD, id, placeId }
}

/**
 * @param {number} id
 * @param {number} tagId
 * @return {{ type: 'ATTACH_TAG_TO_CARD', id: number, tagId: number }}
 */
export function addTag(id, tagId) {
  return { type: ATTACH_TAG_TO_CARD, id, tagId }
}

/**
 * @param {number} id
 * @param {number} characterId
 * @return {{ type: 'REMOVE_CHARACTER_FROM_CARD', id: number, characterId: number }}
 */
export function removeCharacter(id, characterId) {
  return { type: REMOVE_CHARACTER_FROM_CARD, id, characterId }
}

/**
 * @param {number} id
 * @param {number} placeId
 * @return {{ type: 'REMOVE_PLACE_FROM_CARD', id: number, placeId: number }}
 */
export function removePlace(id, placeId) {
  return { type: REMOVE_PLACE_FROM_CARD, id, placeId }
}

/**
 * @param {number} id
 * @param {number} tagId
 * @return {{ type: 'REMOVE_TAG_FROM_CARD', id: number, tagId: number }}
 */
export function removeTag(id, tagId) {
  return { type: REMOVE_TAG_FROM_CARD, id, tagId }
}

/**
 * @param {number} id
 * @param {number} noteId
 * @return {{ type: 'ATTACH_NOTE_TO_CARD', id: number, noteId: number }}
 */
export function addNote(id, noteId) {
  return { type: ATTACH_NOTE_TO_CARD, id, noteId }
}

/**
 * @param {number} id
 * @param {number} noteId
 * @return {{ type: 'REMOVE_NOTE_FROM_CARD', id: number, noteId: number }}
 */
export function removeNote(id, noteId) {
  return { type: REMOVE_NOTE_FROM_CARD, id, noteId }
}

/**
 * @param {number} id
 * @param {String} templateId
 * @return {{ type: 'REMOVE_TEMPLATE_FROM_CARD', id: number, templateId: String }}
 */
export function removeTemplateFromCard(id, templateId) {
  return { type: REMOVE_TEMPLATE_FROM_CARD, id, templateId }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Array<Card>} cards
 * @return {{ type: 'LOAD_CARDS', patching: boolean, cards: Array<Card> }}
 */
export function load(patching, cards) {
  return { type: LOAD_CARDS, patching, cards }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Card} card
 * @return {{ type: 'LOAD_CARD', patching: boolean, card: Card }}
 */
export function loadSingle(patching, card) {
  return { type: LOAD_CARD, patching, card }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Array<Card>} cards
 * @return {{ type: 'BATCH_LOAD_CARD', patching: boolean, cards: Array<Card> }}
 */
export function batchLoad(patching, cards) {
  return { type: BATCH_LOAD_CARD, patching, cards }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Card} card
 * @return {{ type: 'REMOVE_CARD', patching: boolean, card: Card }}
 */
export function removeSingle(patching, card) {
  return { type: REMOVE_CARD, patching, card }
}

/**
 * @param {number} id
 * @return {{ type: 'DUPLICATE_CARD', id: number }}
 */
export function duplicateCard(id) {
  return { type: DUPLICATE_CARD, id }
}

/**
 * @param {number | 'series'} bookId
 * @param {number} cardId
 * @return {{ type: 'MOVE_CARD_TO_BOOK', bookId: number | 'series', cardId: number }}
 */
export function moveCardToBook(bookId, cardId) {
  // NOTE: we rely on the root reducer to compute the destination beat
  // and line ids.
  return { type: MOVE_CARD_TO_BOOK, bookId, cardId }
}

/**
 * @param {number} originalPosition
 * @param {number} destination
 * @param {number} cardId
 * @return {{ type: 'REORDER_CARD_TEMPLATE_ATTRIBUTES', originalPosition: number, destination: number, id: number }}
 */
export const reorderCardTemplateAttribute = (originalPosition, destination, cardId) => {
  return {
    type: REORDER_CARD_TEMPLATE_ATTRIBUTES,
    originalPosition,
    destination,
    id: cardId,
  }
}

/**
 * @param {number} originalPosition
 * @param {number | Null} newPosition
 * @param {number} cardId
 * @param {boolean} moveUp
 * @param {'series' | number} bookId
 * @param {number} beatId
 * @param {Array<Card>} cardsInTargetBeat
 * @return {{ type: 'REORDER_OUTLINE_PLAN_CARDS', originalPosition: number, newPosition: number | Null, cardId: number, moveUp: boolean, bookId: 'series' | number, beatId: number, cardsInTargetBeat: Array<Card> }}
 */
export const _reorderOutlinePlanCards = (
  originalPosition,
  newPosition,
  cardId,
  moveUp,
  bookId,
  beatId,
  cardsInTargetBeat
) => {
  return {
    type: REORDER_OUTLINE_PLAN_CARDS,
    originalPosition,
    newPosition,
    cardId,
    moveUp,
    bookId,
    beatId,
    cardsInTargetBeat,
  }
}

/**
 * @param {number} id
 * @param {'series' | number} bookId
 * @return {{ type: 'AUTO_SORT_BEAT', id: number, bookId: 'series' | number }}
 */
export const _autoSortBeat = (id, bookId) => {
  return { type: AUTO_SORT_BEAT, id, bookId }
}

// ASUME: Cards can't be moved between lines using this action(!!!)
//
// Assumption has implications for reordering the cards within the
// line when a card came from a different beat.
/**
 * @param {{ index: number, positionInBeat: number | Null, cardId: number, beatId: number, lineId: number, positionWithinLine: number }} card
 * @param {number} beatId
 * @param {boolean} moveUp
 * @return {function(Dispatch, GetState): Void}
 */
export const reorderOutlinePlanCards = (card, beatId, moveUp) => (dispatch, getState) => {
  const state = getState()
  const bookId = currentTimelineSelector(state)
  const theBeat = singleBeatInCurrentBookSelector(state, beatId)
  if (theBeat) {
    const cardsInTargetBeat = allCardsInBeatSelector(state, theBeat)

    dispatch(
      _reorderOutlinePlanCards(
        card.index,
        card.positionInBeat,
        card.cardId,
        moveUp,
        bookId,
        beatId,
        cardsInTargetBeat
      )
    )

    // Dropped in from a different beat.  We need to adjust the line
    // positions.
    if (beatId !== card.beatId) {
      const cardIdsWithinLine = cardsInTargetBeat
        .filter(({ lineId }) => {
          return lineId === card.lineId
        })
        .map(({ id }) => {
          return id
        })
      cardIdsWithinLine.splice(card.positionWithinLine, 0, card.cardId)
      dispatch(reorderCardsWithinLine(beatId, card.lineId, cardIdsWithinLine, false))
    }

    dispatch((innerDispatch, innerGetState) => {
      const finalState = innerGetState()
      const finalCardsInTargetBeat = allCardsInBeatSelector(finalState, theBeat)
      const sortedLines = sortedLinesByBookSelector(finalState)
      if (
        isEqual(
          sortCardsInBeat(false, finalCardsInTargetBeat, sortedLines),
          sortCardsInBeat(true, finalCardsInTargetBeat, sortedLines)
        )
      ) {
        innerDispatch(
          // NOTE(ed): we add attributes to the action in the reducer
          //
          // @ts-ignore
          _autoSortBeat(beatId, theBeat.bookId)
        )
      }
    })
  } else {
    // TODO(ed): handle the case that we're reordering outline cards
    // on the plan view and the beat doesn't exist.
  }
}
